<template>
  <div class="view-root">
    <div class="view-header">
      <h2 class="view-title">報表排程寄送</h2>
      <button class="btn btn-primary btn-sm" @click="openCreate">+ 新增排程</button>
    </div>

    <div v-if="loading" class="text-center py-10" style="color:var(--gray-400);">載入中…</div>
    <div v-else-if="!schedules.length" class="empty-state">
      <div style="font-size:2.5rem;margin-bottom:12px;">📊</div>
      <div style="font-weight:700;margin-bottom:6px;">尚無報表排程</div>
      <div style="color:var(--gray-400);font-size:.9rem;">設定定期自動寄送行銷報表到指定信箱</div>
    </div>

    <div v-else class="sched-list">
      <div v-for="s in schedules" :key="s.id" class="sched-card">
        <div class="sched-info">
          <div class="sched-name">{{ s.name }}</div>
          <div class="sched-meta">
            <span class="chip-type">{{ reportTypeLabel(s.report_type) }}</span>
            <span class="chip-freq">{{ freqLabel(s.frequency) }}</span>
            <span class="chip-status" :class="s.active ? 'active' : 'inactive'">
              {{ s.active ? '啟用' : '停用' }}
            </span>
          </div>
          <div class="sched-recipients">
            📧 {{ parseRecipients(s.send_to).join('、') }}
          </div>
          <div class="sched-next" v-if="s.next_send_at">
            下次寄送：{{ fmtDt(s.next_send_at) }}
          </div>
          <div class="sched-last" v-if="s.last_sent_at">
            上次寄送：{{ fmtDt(s.last_sent_at) }}
          </div>
        </div>
        <div class="sched-actions">
          <button class="btn btn-xs btn-secondary" @click="openEdit(s)">✏ 編輯</button>
          <button class="btn btn-xs btn-danger" @click="deleteSched(s)">刪除</button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal=false">
      <div class="modal-box" style="max-width:520px;">
        <div class="modal-header">
          <h3>{{ editingId ? '編輯排程' : '新增報表排程' }}</h3>
          <button class="modal-close" @click="showModal=false">✕</button>
        </div>
        <div class="modal-body space-y-3">
          <div>
            <label class="label">排程名稱 *</label>
            <input v-model="form.name" class="input" placeholder="例：每週行銷摘要">
          </div>
          <div>
            <label class="label">報表類型</label>
            <select v-model="form.report_type" class="input">
              <option value="campaign_summary">行銷活動摘要</option>
              <option value="open_rate">開信率分析</option>
              <option value="subscriber_growth">訂閱者成長</option>
            </select>
          </div>
          <div>
            <label class="label">寄送頻率</label>
            <select v-model="form.frequency" class="input">
              <option value="daily">每日</option>
              <option value="weekly">每週一</option>
              <option value="monthly">每月 1 日</option>
            </select>
          </div>
          <div>
            <label class="label">收件人（每行一個 Email）</label>
            <textarea v-model="recipientsText" class="input" rows="3"
              placeholder="admin@example.com&#10;team@example.com"></textarea>
          </div>
          <div class="flex items-center gap-2">
            <input type="checkbox" v-model="form.active" id="sched-active" style="width:16px;height:16px;">
            <label for="sched-active" style="font-size:.9rem;cursor:pointer;">啟用此排程</label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showModal=false">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="saveSched">
            {{ saving ? '儲存中…' : (editingId ? '更新' : '建立') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import { useAppStore, ADMIN_API } from '../store/app.js'

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` })

const schedules = ref([])
const loading = ref(false)
const showModal = ref(false)
const editingId = ref(null)
const saving = ref(false)
const recipientsText = ref('')

const defaultForm = () => ({
  name: '', report_type: 'campaign_summary', frequency: 'weekly',
  send_to: [], active: true
})
const form = reactive(defaultForm())

const reportTypeLabel = (t) => ({ campaign_summary: '活動摘要', open_rate: '開信率', subscriber_growth: '訂閱成長' }[t] || t)
const freqLabel = (f) => ({ daily: '每日', weekly: '每週', monthly: '每月' }[f] || f)
const parseRecipients = (s) => { try { return JSON.parse(s) } catch { return [] } }

function fmtDt(dt) {
  if (!dt) return ''
  return new Date(dt).toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

async function loadSchedules() {
  loading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/report-schedules`, { headers: authHdr() })
    schedules.value = res.data || []
  } catch (e) {
    window.$toast?.error('載入失敗', e?.response?.data?.detail || e.message)
  } finally {
    loading.value = false
  }
}

function openCreate() {
  Object.assign(form, defaultForm())
  editingId.value = null
  recipientsText.value = ''
  showModal.value = true
}

function openEdit(s) {
  Object.assign(form, {
    name: s.name, report_type: s.report_type, frequency: s.frequency, active: !!s.active
  })
  recipientsText.value = parseRecipients(s.send_to).join('\n')
  editingId.value = s.id
  showModal.value = true
}

async function saveSched() {
  if (!form.name.trim()) return window.$toast?.error('請輸入排程名稱')
  const send_to = recipientsText.value.split('\n').map(s => s.trim()).filter(Boolean)
  if (!send_to.length) return window.$toast?.error('請填寫至少一個收件 Email')
  saving.value = true
  try {
    const payload = { ...form, send_to }
    if (editingId.value) {
      await axios.put(`${ADMIN_API}/ai/report-schedules/${editingId.value}`, payload, { headers: authHdr() })
      window.$toast?.success('已更新')
    } else {
      await axios.post(`${ADMIN_API}/ai/report-schedules`, payload, { headers: authHdr() })
      window.$toast?.success('已建立')
    }
    showModal.value = false
    await loadSchedules()
  } catch (e) {
    window.$toast?.error('儲存失敗', e?.response?.data?.detail || e.message)
  } finally {
    saving.value = false
  }
}

async function deleteSched(s) {
  if (!confirm(`確定刪除排程「${s.name}」？`)) return
  try {
    await axios.delete(`${ADMIN_API}/ai/report-schedules/${s.id}`, { headers: authHdr() })
    window.$toast?.success('已刪除')
    await loadSchedules()
  } catch (e) {
    window.$toast?.error('刪除失敗')
  }
}

onMounted(loadSchedules)
</script>

<style scoped>
.view-root { padding: 24px; max-width: 800px; margin: 0 auto; }
.view-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.view-title { font-size: 1.3rem; font-weight: 800; }
.empty-state { text-align: center; padding: 60px 0; color: var(--gray-400); }
.sched-list { display: flex; flex-direction: column; gap: 12px; }
.sched-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 16px 20px; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.sched-info { flex: 1; display: flex; flex-direction: column; gap: 5px; }
.sched-name { font-size: 1rem; font-weight: 700; }
.sched-meta { display: flex; gap: 8px; flex-wrap: wrap; }
.chip-type, .chip-freq { background: #ede9fe; color: #5b21b6; border-radius: 5px; padding: 2px 9px; font-size: .75rem; font-weight: 600; }
.chip-freq { background: #dbeafe; color: #1e40af; }
.chip-status { border-radius: 5px; padding: 2px 9px; font-size: .75rem; font-weight: 600; }
.chip-status.active { background: #dcfce7; color: #166534; }
.chip-status.inactive { background: var(--gray-100); color: var(--gray-500); }
.sched-recipients { font-size: .83rem; color: var(--gray-600); }
.sched-next, .sched-last { font-size: .78rem; color: var(--gray-400); }
.sched-actions { display: flex; gap: 8px; flex-shrink: 0; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; }
.modal-box { background: var(--surface); border-radius: 16px; width: 100%; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.2); }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 0; }
.modal-header h3 { font-size: 1.1rem; font-weight: 700; }
.modal-close { background: none; border: none; font-size: 1.1rem; cursor: pointer; color: var(--gray-400); }
.modal-body { padding: 16px 24px; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px 20px; border-top: 1px solid var(--border); }
.label { display: block; font-size: .8rem; font-weight: 700; color: var(--gray-600); margin-bottom: 4px; }
.space-y-3 > * + * { margin-top: 12px; }
.flex { display: flex; }
.items-center { align-items: center; }
.gap-2 { gap: 8px; }
</style>
